import { Component, OnInit, inject, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { EvenementService } from '../../../_services/evenement/evenement.service';
import { InscriptionService } from '../../../_services/inscription/inscription.service';
import { Evenement } from '../../../_interfaces/evenement';
import { Inscription } from '../../../_interfaces/inscription';
import { AdminPageHeaderComponent } from '../../../_components/admin-page-header/admin-page-header.component';

interface ClassementMembre {
  utilisateur_uid: string;
  nom_prenom: string;
  count: number;
}

interface TopEvenement {
  key: string;
  nom: string;
  dateDebut: string;
  count: number;
}

function toDateInputValue(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

@Component({
    selector: 'app-statistiques',
    imports: [CommonModule, FormsModule, AdminPageHeaderComponent],
    templateUrl: './statistiques.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: './statistiques.component.scss'
})
export class StatistiquesComponent implements OnInit {
  private evenementService = inject(EvenementService);
  private inscriptionService = inject(InscriptionService);

  evenements: Evenement[] = [];
  inscriptions: Inscription[] = [];
  loading = true;

  dateDebut = '';
  dateFin = '';
  activePreset: 'annee' | '12mois' | 'tout' | 'custom' = 'tout';

  eventsInRange: Evenement[] = [];
  classement: ClassementMembre[] = [];
  topEvenements: TopEvenement[] = [];

  nbSorties = 0;
  nbPresences = 0;
  nbParticipantsUniques = 0;
  moyennePresencesParSortie = 0;

  ngOnInit() {
    this.evenementService.readAllEvenements().subscribe(evenements => {
      this.evenements = evenements;
      if (!this.dateDebut || !this.dateFin) {
        this.applyPreset('tout');
      } else {
        this.recompute();
      }
      this.loading = false;
    });

    this.inscriptionService.readAllInscriptions().then(inscriptions => {
      this.inscriptions = inscriptions;
      this.recompute();
    });
  }

  applyPreset(preset: 'annee' | '12mois' | 'tout') {
    this.activePreset = preset;
    const now = new Date();

    if (preset === 'annee') {
      this.dateDebut = toDateInputValue(new Date(now.getFullYear(), 0, 1));
      this.dateFin = toDateInputValue(now);
    } else if (preset === '12mois') {
      const start = new Date(now);
      start.setFullYear(start.getFullYear() - 1);
      this.dateDebut = toDateInputValue(start);
      this.dateFin = toDateInputValue(now);
    } else {
      const dates = this.evenements.map(e => new Date(e.dateDebut).getTime());
      const min = dates.length ? new Date(Math.min(...dates)) : new Date(now.getFullYear(), 0, 1);
      this.dateDebut = toDateInputValue(min);
      this.dateFin = toDateInputValue(now);
    }
    this.recompute();
  }

  onCustomRangeChange() {
    this.activePreset = 'custom';
    this.recompute();
  }

  private recompute() {
    if (!this.dateDebut || !this.dateFin) return;

    const start = new Date(this.dateDebut);
    start.setHours(0, 0, 0, 0);
    const end = new Date(this.dateFin);
    end.setHours(23, 59, 59, 999);

    this.eventsInRange = this.evenements.filter(e => {
      const d = new Date(e.dateDebut);
      return d >= start && d <= end;
    });
    this.nbSorties = this.eventsInRange.length;

    const eventKeysInRange = new Set(this.eventsInRange.map(e => e.key));
    const presencesInRange = this.inscriptions.filter(
      i => i.is_present && eventKeysInRange.has(i.evenement_key)
    );
    this.nbPresences = presencesInRange.length;

    const parMembre = new Map<string, ClassementMembre>();
    for (const inscription of presencesInRange) {
      const existing = parMembre.get(inscription.utilisateur_uid);
      if (existing) {
        existing.count++;
      } else {
        parMembre.set(inscription.utilisateur_uid, {
          utilisateur_uid: inscription.utilisateur_uid,
          nom_prenom: inscription.nom_prenom,
          count: 1
        });
      }
    }
    this.classement = Array.from(parMembre.values()).sort((a, b) => b.count - a.count);
    this.nbParticipantsUniques = this.classement.length;
    this.moyennePresencesParSortie = this.nbSorties > 0 ? this.nbPresences / this.nbSorties : 0;

    const parEvenement = new Map<string, number>();
    for (const inscription of presencesInRange) {
      parEvenement.set(inscription.evenement_key, (parEvenement.get(inscription.evenement_key) ?? 0) + 1);
    }
    this.topEvenements = this.eventsInRange
      .filter(e => e.key && parEvenement.has(e.key))
      .map(e => ({
        key: e.key as string,
        nom: e.nom,
        dateDebut: e.dateDebut,
        count: parEvenement.get(e.key as string) ?? 0
      }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);
  }

  maxClassementCount(): number {
    return this.classement.length ? this.classement[0].count : 1;
  }

  barWidth(count: number): number {
    const max = this.maxClassementCount();
    return max === 0 ? 0 : Math.max(4, Math.round((count / max) * 100));
  }

  formatDate(value: string | undefined): string {
    if (!value) return '';
    const d = new Date(value);
    return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
  }
}
